import React, { useState, useEffect } from 'react';
import api from '../services/api';
import AdminLayout from '../components/Layout/AdminLayout';

function AppSettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSetting, setNewSetting] = useState({
    setting_key: '',
    setting_value: '',
    setting_description: '',
    setting_type: 'text',
    is_active: true,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/app-settings');
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleEdit = (setting) => {
    setEditingId(setting.id);
    setEditValue(setting.setting_value);
    setEditDescription(setting.setting_description || '');
  };

  const handleSave = async (id) => {
    try {
      await api.put(`/app-settings/${id}`, {
        setting_value: editValue,
        setting_description: editDescription,
      });
      await fetchSettings();
      setEditingId(null);
      alert('Setting updated successfully!');
    } catch (error) {
      console.error('Error updating setting:', error);
      alert('Failed to update setting');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
    setEditDescription('');
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/app-settings/${id}`, {
        is_active: !currentStatus,
      });
      await fetchSettings();
      alert('Setting status updated!');
    } catch (error) {
      console.error('Error toggling setting:', error);
      alert('Failed to toggle setting status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this setting?')) {
      return;
    }

    try {
      await api.delete(`/app-settings/${id}`);
      await fetchSettings();
      alert('Setting deleted successfully!');
    } catch (error) {
      console.error('Error deleting setting:', error);
      alert('Failed to delete setting');
    }
  };

  const handleAddSetting = async (e) => {
    e.preventDefault();
    try {
      await api.post('/app-settings', newSetting);
      await fetchSettings();
      setShowAddModal(false);
      setNewSetting({
        setting_key: '',
        setting_value: '',
        setting_description: '',
        setting_type: 'text',
        is_active: true,
      });
      alert('Setting added successfully!');
    } catch (error) {
      console.error('Error adding setting:', error);
      alert('Failed to add setting: ' + (error.response?.data?.message || error.message));
    }
  };

  const getSettingIcon = (type) => {
    switch (type) {
      case 'youtube_playlist':
        return '📺';
      case 'youtube_channel':
        return '▶️';
      case 'url':
        return '🔗';
      case 'number':
        return '🔢';
      case 'boolean':
        return '✓';
      default:
        return '📝';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">إعدادات التطبيق</h1>
            <p className="text-text-secondary mt-1">إدارة إعدادات التطبيق الديناميكية</p>
          </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-accent-gold text-primary-dark px-4 py-2 rounded-lg hover:bg-yellow-500 transition-colors font-semibold"
        >
          + إضافة إعداد
        </button>
      </div>

      {/* Settings Cards */}
      <div className="grid gap-4">
        {settings.map((setting) => (
          <div
            key={setting.id}
            className={`bg-card rounded-lg shadow p-6 border-r-4 ${
              setting.is_active ? 'border-green-500' : 'border-gray-400'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{getSettingIcon(setting.setting_type)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {setting.setting_key}
                    </h3>
                    <span
                      className={`inline-block px-2 py-1 text-xs rounded ${
                        setting.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {setting.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {setting.setting_type}
                    </span>
                  </div>
                </div>

                {editingId === setting.id ? (
                  <div className="space-y-3 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Value
                      </label>
                      <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Setting value"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (Optional)
                      </label>
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="2"
                        placeholder="Description"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSave(setting.id)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancel}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3">
                    <p className="text-sm text-text-secondary mb-1">القيمة:</p>
                    <code className="block bg-primary-light px-3 py-2 rounded text-sm text-text-primary break-all">
                      {setting.setting_value}
                    </code>
                    {setting.setting_description && (
                      <p className="text-sm text-text-secondary mt-2">
                        {setting.setting_description}
                      </p>
                    )}
                    <div className="text-xs text-text-muted mt-2">
                      آخر تحديث: {new Date(setting.updated_at).toLocaleString('ar')}
                    </div>
                  </div>
                )}
              </div>

              {editingId !== setting.id && (
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(setting)}
                    className="text-blue-600 hover:text-blue-800 px-3 py-1 rounded hover:bg-blue-50"
                    title="Edit"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(setting.id, setting.is_active)}
                    className={`px-3 py-1 rounded ${
                      setting.is_active
                        ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'
                        : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                    }`}
                    title={setting.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {setting.is_active ? '⏸️ Disable' : '▶️ Enable'}
                  </button>
                  <button
                    onClick={() => handleDelete(setting.id)}
                    className="text-red-600 hover:text-red-800 px-3 py-1 rounded hover:bg-red-50"
                    title="Delete"
                  >
                    🗑️ Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {settings.length === 0 && (
        <div className="text-center py-12 bg-primary-light rounded-lg">
          <p className="text-text-secondary">لا توجد إعدادات. أضف أول إعداد!</p>
        </div>
      )}

      {/* Add Setting Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4 text-text-primary">إضافة إعداد جديد</h2>
            <form onSubmit={handleAddSetting} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Setting Key *
                </label>
                <input
                  type="text"
                  value={newSetting.setting_key}
                  onChange={(e) =>
                    setNewSetting({ ...newSetting, setting_key: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., gomaa_youtube_playlist_id"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Value *
                </label>
                <input
                  type="text"
                  value={newSetting.setting_value}
                  onChange={(e) =>
                    setNewSetting({ ...newSetting, setting_value: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Setting value"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newSetting.setting_description}
                  onChange={(e) =>
                    setNewSetting({ ...newSetting, setting_description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Optional description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={newSetting.setting_type}
                  onChange={(e) =>
                    setNewSetting({ ...newSetting, setting_type: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="text">Text</option>
                  <option value="youtube_playlist">YouTube Playlist</option>
                  <option value="youtube_channel">YouTube Channel</option>
                  <option value="url">URL</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-accent-gold text-primary-dark px-4 py-2 rounded-lg hover:bg-yellow-500 font-semibold"
                >
                  إضافة
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
}

export default AppSettings;
